import { type FilterGroup, type Locale, type SearchCategory } from '@kijiji/generated/graphql-types'
import { type NextSeoProps } from 'next-seo'

import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { type ApiLocale, API_LOCALE } from '@/domain/locale'
import { getMetaTitleDescriptionH1 } from '@/domain/srp/seo/getMetaTitleDescriptionH1'
import { getSrpCanonicalUrl } from '@/domain/srp/seo/getSrpCanonicalUrl'
import { getSrpStaticH1 } from '@/domain/srp/seo/getSrpStaticH1'
import { type Listing } from '@/types/search'
import { type IntlResponse } from '@/types/translations'
import { createRobotsContent } from '@/utils/seo'

export type RobotsProps = {
  noindex: boolean
  nofollow: boolean
}

type SrpMetadataResponse = NextSeoProps & {
  h1: string | IntlResponse
}

export type SrpMetaDataProps = {
  apiLocale: ApiLocale
  category: SearchCategory
  currentPage: number
  filters: FilterGroup[]
  firstListing?: Listing
  keyword: string | null
  location?: Locale | null
  totalListingsCount: number
  url: URL
}

export const getSrpMetaData = ({
  apiLocale,
  category,
  currentPage,
  filters,
  firstListing,
  keyword,
  location,
  totalListingsCount,
  url,
}: SrpMetaDataProps): SrpMetadataResponse => {
  /** Page title & description comes from an excel sheet */
  const { description, title, dynamicH1 } = getMetaTitleDescriptionH1({
    apiLocale,
    category,
    filters,
    keyword,
    location,
    totalListingsCount,
  })

  // If category is "All Categories" and the language is FR, then H1 should have "Toutes" before the localized name in the static H1
  const categoryName =
    category.id === ALL_CATEGORIES_ID_NUM && apiLocale === API_LOCALE.fr_CA
      ? `Toutes ${category.localizedName}`
      : category.localizedName

  /** It should only get static H1 if no dynamic was found */
  const pageH1 =
    dynamicH1 ||
    getSrpStaticH1({
      categoryId: category.id,
      categoryName,
      keyword,
      locationName: `${location?.[apiLocale]}`,
      total: totalListingsCount,
    })

  /**
   * The SEO will get some information from the first listings of the SRP:
   * - title & Image
   */
  const seoImages = firstListing?.imageUrls[0]
    ? [{ url: firstListing.imageUrls[0], alt: firstListing.title }]
    : []

  const ogMetaData = { openGraph: { url: url.href, images: seoImages } }

  const canonicalUrl = getSrpCanonicalUrl(url, category)

  /** Define meta robots based on total count */
  const robotsContent: RobotsProps = createRobotsContent(totalListingsCount)

  /** Display page number on title */
  const displayPageNumber = currentPage > 1 ? ` - Page ${currentPage}` : ''

  const seo: SrpMetadataResponse = {
    description,
    title: `${title}${displayPageNumber}`,
    h1: pageH1,
    canonical: canonicalUrl,
    ...robotsContent,
    ...ogMetaData,
  }

  return seo
}
